'use strict';

const CONSENT_CATEGORY_NECESSARY = 'necessary';
const DEFAULT_SALE_SHARE_COOKIE_CATEGORIES = ['marketing'];

/**
 * @returns {string[]}
 */
function saleShareCategoriesForStore() {
	const configured = globalThis.rp_app?.consent?.saleShareCategories;
	const source = Array.isArray(configured) && configured.length > 0
		? configured
		: DEFAULT_SALE_SHARE_COOKIE_CATEGORIES;

	return source.filter(function (category) {
		return typeof category === 'string' && category !== CONSENT_CATEGORY_NECESSARY;
	});
}

/**
 * @returns {boolean}
 */
function isGlobalPrivacyControlEnabled() {
	return globalThis.navigator?.globalPrivacyControl === true;
}

/**
 * @returns {{acceptCategory?: Function, getCookie?: Function, getUserPreferences?: Function, setCookieData?: Function}|null}
 */
function getCookieConsentInstance() {
	const cookieConsent = globalThis.CookieConsent;
	if (!cookieConsent || typeof cookieConsent.getUserPreferences !== 'function') {
		return null;
	}

	return cookieConsent;
}

/**
 * @param {ReturnType<typeof getCookieConsentInstance>} cookieConsent
 * @returns {{
 *   gpcHonoredAt?: string,
 *   saleShareOptedOut?: boolean,
 *   saleShareOptedOutAt?: string|null,
 *   sensitivePiLimited?: boolean,
 * }}
 */
function readPrivacyRightsData(cookieConsent) {
	if (!cookieConsent || typeof cookieConsent.getCookie !== 'function') {
		return {};
	}

	const cookie = cookieConsent.getCookie('data');
	if (!cookie || typeof cookie !== 'object') {
		return {};
	}

	return cookie;
}

/**
 * @param {ReturnType<typeof getCookieConsentInstance>} cookieConsent
 * @returns {boolean}
 */
function isPrivacySaleShareOptedOut(cookieConsent) {
	if (isGlobalPrivacyControlEnabled()) {
		return true;
	}

	return readPrivacyRightsData(cookieConsent).saleShareOptedOut === true;
}

/**
 * @param {ReturnType<typeof getCookieConsentInstance>} cookieConsent
 * @returns {boolean}
 */
function hasPrivacySaleShareChoice(cookieConsent) {
	if (isGlobalPrivacyControlEnabled()) {
		return true;
	}

	return typeof readPrivacyRightsData(cookieConsent).saleShareOptedOut === 'boolean';
}

/**
 * @param {string[]} cookieAllowedCategories
 * @param {ReturnType<typeof getCookieConsentInstance>} cookieConsent
 * @returns {string[]}
 */
function applyPrivacyRightsToAllowedCategories(cookieAllowedCategories, cookieConsent) {
	// Persisted Your Privacy Choices only. Live GPC is out of this storefront apply path.
	if (readPrivacyRightsData(cookieConsent).saleShareOptedOut !== true) {
		return cookieAllowedCategories;
	}

	const consent = globalThis.rp_app?.consent;
	if (!consent || typeof consent.rpCategoriesForCookieCategories !== 'function') {
		return cookieAllowedCategories;
	}

	const saleShareRpCategories = consent.rpCategoriesForCookieCategories(
		saleShareCategoriesForStore(),
	);

	return cookieAllowedCategories.filter(function (category) {
		return category === 'essential' || !saleShareRpCategories.includes(category);
	});
}

/**
 * @param {string[]} cookieCategories
 * @param {ReturnType<typeof getCookieConsentInstance>} [cookieConsent]
 */
function applyEffectiveConsentFromCookieCategories(cookieCategories, cookieConsent) {
	const consent = globalThis.rp_app?.consent;
	const cmp = cookieConsent ?? getCookieConsentInstance();
	if (!consent || typeof consent.rpCategoriesForCookieCategories !== 'function') {
		return;
	}

	const cookieAllowedCategories = consent.rpCategoriesForCookieCategories(cookieCategories);
	const effectiveAllowedCategories = applyPrivacyRightsToAllowedCategories(
		cookieAllowedCategories,
		cmp,
	);

	if (typeof consent.setAllowedConsentCategories === 'function') {
		consent.setAllowedConsentCategories(effectiveAllowedCategories);
	}

	if (typeof consent.updateGoogleConsentModeFromAllowedConsentCategories === 'function') {
		consent.updateGoogleConsentModeFromAllowedConsentCategories(effectiveAllowedCategories);
	}

	if (typeof consent.activateConsentBlockedScripts === 'function') {
		consent.activateConsentBlockedScripts(effectiveAllowedCategories);
	}
}

/**
 * @param {ReturnType<typeof getCookieConsentInstance>} [cookieConsent]
 */
function reapplyEffectiveConsent(cookieConsent) {
	const cmp = cookieConsent ?? getCookieConsentInstance();
	const cookieCategories = cmp?.getUserPreferences?.()?.acceptedCategories;
	if (!Array.isArray(cookieCategories)) {
		return;
	}

	applyEffectiveConsentFromCookieCategories(cookieCategories, cmp);
}

/**
 * @param {ReturnType<typeof getCookieConsentInstance>} cookieConsent
 * @param {boolean} optedOut
 */
function persistPrivacySaleShareOptOut(cookieConsent, optedOut) {
	if (!cookieConsent || typeof cookieConsent.setCookieData !== 'function') {
		return;
	}

	const nowIso = new Date().toISOString();
	const value = {
		saleShareOptedOut: optedOut,
		saleShareOptedOutAt: optedOut ? nowIso : null,
	};
	if (!optedOut) {
		value.gpcHonoredAt = null;
	}
	cookieConsent.setCookieData({
		mode: 'update',
		value: value,
	});

	reapplyEffectiveConsent(cookieConsent);
}

/**
 * @param {ReturnType<typeof getCookieConsentInstance>} cookieConsent
 * @param {boolean} limited
 */
function persistPrivacySensitivePiLimit(cookieConsent, limited) {
	if (!cookieConsent || typeof cookieConsent.setCookieData !== 'function') {
		return;
	}

	cookieConsent.setCookieData({
		mode: 'update',
		value: {
			sensitivePiLimited: limited,
		},
	});
}

/**
 * @param {ReturnType<typeof getCookieConsentInstance>} cookieConsent
 * @returns {boolean}
 */
function isPrivacySensitivePiLimited(cookieConsent) {
	return readPrivacyRightsData(cookieConsent).sensitivePiLimited === true;
}

/**
 * @param {ReturnType<typeof getCookieConsentInstance>} cookieConsent
 * @returns {boolean}
 */
function honorGlobalPrivacyControlIfEnabled(cookieConsent) {
	if (!cookieConsent || !isGlobalPrivacyControlEnabled()) {
		return false;
	}

	const data = readPrivacyRightsData(cookieConsent);
	if (data.saleShareOptedOut === true || typeof data.gpcHonoredAt === 'string') {
		return false;
	}

	persistPrivacySaleShareOptOut(cookieConsent, true);

	if (typeof cookieConsent.setCookieData === 'function') {
		cookieConsent.setCookieData({
			mode: 'update',
			value: {
				gpcHonoredAt: new Date().toISOString(),
			},
		});
	}

	reapplyEffectiveConsent(cookieConsent);

	return true;
}

const privacyRightsApi = {
	applyEffectiveConsentFromCookieCategories,
	applyPrivacyRightsToAllowedCategories,
	getCookieConsentInstance,
	hasPrivacySaleShareChoice,
	honorGlobalPrivacyControlIfEnabled,
	isGlobalPrivacyControlEnabled,
	isPrivacySaleShareOptedOut,
	isPrivacySensitivePiLimited,
	persistPrivacySaleShareOptOut,
	persistPrivacySensitivePiLimit,
	readPrivacyRightsData,
	reapplyEffectiveConsent,
	saleShareCategoriesForStore,
};

globalThis.rp_app = globalThis.rp_app || {};
globalThis.rp_app.consent = globalThis.rp_app.consent || {};
Object.assign(globalThis.rp_app.consent, privacyRightsApi);

if (typeof module !== 'undefined' && module.exports) {
	module.exports = privacyRightsApi;
}
